import type { KnowledgeEntry, KnowledgeJoinKeep, KnowledgeKind } from '@ni/shared';

/**
 * **What the join's search may offer**, which is only ever people
 * (organisations-as-subjects note §2 chunk 1, "Join by hand").
 *
 * The endpoint refuses everything else, and the search is narrowed to match
 * rather than left wide and refused on the press: a list offering an
 * organisation the office can pick, and then a refusal for having picked it, is
 * a door that lies about what it does.
 */
export const JOIN_SEARCH_KINDS: readonly KnowledgeKind[] = ['person'];

/**
 * The results as the modal lists them: the record the door was opened from taken
 * out, because a record cannot be joined with itself and the server refuses it —
 * the office should not be able to pick it in the first place.
 *
 * Stubs and redirects never come back from the search at all (the server's
 * `LIVE_ROW`), so there is nothing here about them.
 */
export function joinCandidates(
  entries: readonly KnowledgeEntry[],
  hereEntityId: string,
): KnowledgeEntry[] {
  return entries.filter((entry) => entry.entity_id !== hereEntityId);
}

/** One side of the choice, as much of it as the default depends on. */
export interface JoinSide {
  readonly display_name: string;
  /** Whether the office already keeps a record of this person. */
  readonly tracked: boolean;
}

/**
 * **Which of the two the modal offers to keep**, before the office touches
 * anything — the merge card's `keepNameChoice` rule, reduced to the two signals
 * this door actually has.
 *
 * The card has a `signal` to reason from: the probe said *why* it thinks these
 * two are one, and "contains" or "nickname" is what makes the longer name the
 * fuller spelling rather than one of two ways of writing it. Nothing said
 * anything here — that is the whole reason this door exists — so what is left
 * is:
 *
 *  - **the tracked one, when exactly one of them is.** It is the stronger of the
 *    two rules and it goes first, because it is not really a preference: a
 *    tracked record carries research, notes and follow-ups, the merge refuses to
 *    fold one away, and offering it as the loser would be offering a press whose
 *    only outcome is a refusal.
 *  - **otherwise the longer name**, which on this door is nearly always the
 *    fuller one: the case it was built for is a bare "Clint" beside "Clint
 *    Phillips", and the office is not here to fold a full name into a first
 *    name. A misspelling is the same length as what it misspells, near enough,
 *    which is the case the rule below settles.
 *  - **otherwise the record whose page this is.** There is nothing in the pair
 *    that distinguishes them, and a stable answer beats an arbitrary-looking
 *    one; the reader picks the other with one click either way.
 *
 * A default and not a decision. Both radios are always shown, always by name,
 * and the office can change it — this only decides which one is already filled
 * in when the choice appears.
 *
 * A function rather than an `if` in the component, so all three rules can be
 * pinned without a DOM: this package's suite has no click.
 */
export function joinKeepDefault(here: JoinSide, other: JoinSide): KnowledgeJoinKeep {
  if (here.tracked !== other.tracked) return here.tracked ? 'this' : 'other';
  return here.display_name.length >= other.display_name.length ? 'this' : 'other';
}
